<template>
  <div>
    <div class="mb-6">
      <h2 class="text-2xl font-semibold text-gray-200">Gestion des congres</h2>
      <p class="mt-1 text-sm/6 text-gray-300">Creer, modifier et supprimer un congres avec son PDF.</p>
    </div>

    <div class="bg-gray-900 p-6 rounded-xl border-gray-700 border shadow mb-8">
      <h3 class="text-xl text-gray-100 mb-4">Nouveau congres</h3>
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <label class="text-sm text-white">Nom du congres</label>
          <input
            v-model="newCongresName"
            type="text"
            placeholder="Ex: Marseille 2027"
            class="mt-2 px-4 py-3 block w-full rounded-md border-0 bg-white/5 text-white shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-green-500 sm:text-sm/6"
          />
        </div>
        <div>
          <label class="text-sm text-white">PDF du congres (optionnel)</label>
          <input
            type="file"
            accept="application/pdf"
            @change="handleCreatePdfChange"
            class="mt-2 px-4 py-2 block w-full rounded-md border-0 bg-white/5 text-white shadow-sm ring-1 ring-inset ring-white/10"
          />
        </div>
      </div>
      <div class="mt-4 flex justify-end">
        <button
          @click="createCongres"
          type="button"
          class="rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500"
        >
          Creer le congres
        </button>
      </div>
    </div>

    <div class="space-y-5">
      <div v-for="congre in congresList" :key="congre.id" class="bg-gray-900 p-6 rounded-xl border border-gray-700">
        <div class="flex items-start justify-between gap-4 flex-wrap">
          <div class="flex-1 min-w-[280px]">
            <label class="text-sm text-white">Nom du congres</label>
            <input
              v-model="congre.nom"
              type="text"
              class="mt-2 px-4 py-3 block w-full rounded-md border-0 bg-white/5 text-white shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-green-500 sm:text-sm/6"
            />
          </div>

          <div class="min-w-[280px] flex-1">
            <label class="text-sm text-white">Remplacer le PDF</label>
            <input
              type="file"
              accept="application/pdf"
              @change="(event) => handleUpdatePdfChange(congre.id, event)"
              class="mt-2 px-4 py-2 block w-full rounded-md border-0 bg-white/5 text-white shadow-sm ring-1 ring-inset ring-white/10"
            />
            <p class="text-xs text-gray-400 mt-2" v-if="congre.link">
              PDF actuel:
              <a :href="congre.link" target="_blank" class="text-indigo-300 hover:text-indigo-200">ouvrir</a>
            </p>
            <p class="text-xs text-gray-400 mt-2" v-else>Aucun PDF associe</p>
          </div>
        </div>

        <div class="mt-4 flex items-center justify-between gap-2 flex-wrap">
          <div class="text-xs text-gray-400">Gestion PDF uniquement</div>
          <div class="flex items-center gap-2">
            <button
              @click="updateCongres(congre)"
              type="button"
              class="rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white hover:bg-green-500"
            >
              Enregistrer
            </button>
            <button
              @click="deleteCongres(congre.id)"
              type="button"
              class="rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-500"
            >
              Supprimer
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

const congresList = ref([])
const newCongresName = ref('')
const newCongresPdf = ref(null)
const updatePdfFiles = ref({})

const fetchCongres = async () => {
  try {
    const response = await fetch('https://api.meritemaritime-fnmm.com/admin/congre')
    if (!response.ok) throw new Error('Erreur chargement congres')
    congresList.value = await response.json()
  } catch (error) {
    console.error(error)
  }
}

const handleCreatePdfChange = (event) => {
  newCongresPdf.value = event.target.files[0] || null
}

const handleUpdatePdfChange = (congreId, event) => {
  updatePdfFiles.value[congreId] = event.target.files[0] || null
}

const createCongres = async () => {
  const nom = newCongresName.value.trim()
  if (!nom) {
    alert('Veuillez saisir un nom de congres.')
    return
  }

  const formData = new FormData()
  formData.append('nom', nom)
  if (newCongresPdf.value) {
    formData.append('pdf', newCongresPdf.value)
  }

  try {
    const response = await fetch('https://api.meritemaritime-fnmm.com/admin/congre', {
      method: 'POST',
      body: formData,
    })

    if (!response.ok) throw new Error('Erreur creation congres')

    const created = await response.json()
    congresList.value.unshift(created)
    newCongresName.value = ''
    newCongresPdf.value = null
    alert('Congres cree avec succes.')
  } catch (error) {
    console.error(error)
    alert('Erreur lors de la creation du congres.')
  }
}

const updateCongres = async (congre) => {
  const nom = (congre.nom || '').trim()
  if (!nom) {
    alert('Le nom du congres est obligatoire.')
    return
  }

  const formData = new FormData()
  formData.append('nom', nom)
  if (updatePdfFiles.value[congre.id]) {
    formData.append('pdf', updatePdfFiles.value[congre.id])
  }

  try {
    const response = await fetch(`https://api.meritemaritime-fnmm.com/admin/congre/${congre.id}`, {
      method: 'PUT',
      body: formData,
    })

    if (!response.ok) throw new Error('Erreur update congres')

    const updated = await response.json()
    const idx = congresList.value.findIndex((item) => item.id === congre.id)
    if (idx !== -1) {
      congresList.value[idx] = {
        ...congresList.value[idx],
        nom: updated.nom,
        link: updated.link,
      }
    }

    updatePdfFiles.value[congre.id] = null
    alert('Congres modifie avec succes.')
  } catch (error) {
    console.error(error)
    alert('Erreur lors de la modification du congres.')
  }
}

const deleteCongres = async (congreId) => {
  if (!confirm('Supprimer ce congres et ses images associees ?')) return

  try {
    const response = await fetch(`https://api.meritemaritime-fnmm.com/admin/congre/${congreId}`, {
      method: 'DELETE',
    })

    if (!response.ok) throw new Error('Erreur suppression congres')

    congresList.value = congresList.value.filter((item) => item.id !== congreId)
    alert('Congres supprime avec succes.')
  } catch (error) {
    console.error(error)
    alert('Erreur lors de la suppression du congres.')
  }
}

onMounted(() => {
  fetchCongres()
})
</script>
